import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Typography,
  useTheme,
  Divider,
  MenuItem,
  IconButton,
  Menu,
} from '@ublend-npm/aulaui-core';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { toggleWidgetPreference } from '../../../../../actions/home/widgetPreferences';
import StarredSpaceSkeleton from './StarredSpaceSkeleton';
import StarredSpaceItem from './StarredSpaceItem';
import { mockStarredSpaces } from './mockData';
import { getStarredSpaceColors } from '../../colors';
import { Space } from './Spaces.types';

const STRINGS = {
  emptyState: 'No starred spaces.',
  title: 'Starred spaces',
  hide: 'Hide',
  viewAll: 'View all starred spaces',
  widgetKey: 'home-starred-spaces-widget',
};

const SKELETON_KEYS = ['skeleton-0', 'skeleton-1', 'skeleton-2'];

const StarredSpacesWidget = () => {
  const { palette } = useTheme();
  const dispatch = useDispatch<any>();

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500); // Fake a loading delay of 1.5s
    return () => clearTimeout(timer);
  }, []);

  const [spaces, setSpaces] = useState<Space[]>(mockStarredSpaces);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleHideWidget = () => {
    dispatch(toggleWidgetPreference(STRINGS.widgetKey, true));
    handleCloseMenu();
  };

  const handleUnstar = useCallback((id: string) => {
    setSpaces((prevSpaces) => prevSpaces.filter((space) => space.id !== id));
  }, []);

  const content = useMemo(() => {
    if (isLoading) {
      return SKELETON_KEYS.map((key, index) => (
        <React.Fragment key={key}>
          <StarredSpaceSkeleton />
          {index < 2 && <Divider />}
        </React.Fragment>
      ));
    }

    if (spaces.length === 0) {
      return (
        <Typography
          variant="body2"
          sx={{ color: getStarredSpaceColors(palette).textSecondary }}
        >
          {STRINGS.emptyState}
        </Typography>
      );
    }

    return spaces.map((space) => (
      <React.Fragment key={space.id}>
        <StarredSpaceItem space={space} onUnstar={handleUnstar} />
        <Divider />
      </React.Fragment>
    ));
  }, [isLoading, spaces, handleUnstar]);

  return (
    <Card
      sx={{
        width: '100%',
        borderRadius: '0.75rem',
        padding: { xs: '1rem', md: '1.5rem' },
        boxShadow: 'none',
        border: `1px solid ${palette.divider}`,
        bgcolor: palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: '600px',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        mt={-0.5}
      >
        <Typography variant="subtitle1" fontWeight="medium" color="primary">
          {STRINGS.title}
        </Typography>

        <Box>
          <IconButton
            aria-label="more"
            id="starred-spaces-menu-button"
            aria-controls={isMenuOpen ? 'starred-spaces-menu' : undefined}
            aria-expanded={isMenuOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleClickMenu}
            size="small"
            sx={{ color: getStarredSpaceColors(palette).textSecondary }}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="starred-spaces-menu"
            MenuListProps={{
              'aria-labelledby': 'starred-spaces-menu-button',
            }}
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleCloseMenu}
          >
            <MenuItem label={STRINGS.hide} onClick={handleHideWidget} />
          </Menu>
        </Box>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        flex={1}
        sx={{
          minWidth: 0,
          width: '100%',
        }}
      >
        {content}
      </Box>

      <Button
        fullWidth
        kind="secondary"
        endIcon={<ArrowForwardIcon />}
        sx={{
          width: '100%',
          color: getStarredSpaceColors(palette).starIcon,
        }}
        onClick={() => {
          /* TODO: Implement navigation */
        }}
      >
        {STRINGS.viewAll}
      </Button>
    </Card>
  );
};

export default StarredSpacesWidget;
